import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useWebSocket } from '../../hooks/useWebSocket';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { getWebSocketHttpBase } from '../../lib/apiBase';
import SoftAurora from '../SoftAurora';
import { WelcomeGreeting } from '../WelcomeGreeting';
import { VAssistant } from '../VAssistant';
import { LegalFooter } from '../LegalFooter';
import { cn } from '../../lib/utils';

export function DashboardLayout({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const wsUrl = `${getWebSocketHttpBase()}/ws`;
  const { isConnected, lastMessage } = useWebSocket(wsUrl);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading AERTA...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="relative isolate min-h-screen overflow-x-hidden">
      <WelcomeGreeting user={user} />
      <VAssistant />
      {/* Must sit at z-0, not negative z-index: otherwise parent bg would paint above and hide the canvas */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-90 dark:opacity-100"
        aria-hidden
      >
        {prefersReducedMotion ? (
          <div className="aerta-aurora absolute inset-0 animate-aurora-shift motion-reduce:animate-none" />
        ) : (
          <div className="absolute inset-0 min-h-full origin-center will-change-[opacity,transform] animate-aurora-alive motion-reduce:animate-none">
            <SoftAurora
              speed={0.45}
              scale={1.4}
              brightness={0.82}
              color1="#0f1b33"
              color2="#818cf8"
              noiseFrequency={2.2}
              noiseAmplitude={0.95}
              bandHeight={0.45}
              bandSpread={0.95}
              octaveDecay={0.12}
              layerOffset={0.15}
              colorSpeed={0.85}
              enableMouseInteraction={false}
              mouseInfluence={0}
            />
          </div>
        )}
      </div>
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((c) => !c)} />
      <TopBar
        wsConnected={isConnected}
        lastWsMessage={lastMessage}
        wsUrl={wsUrl}
        sidebarCollapsed={sidebarCollapsed}
      />
      <main
        className={cn(
          'relative z-10 min-h-screen bg-background/55 pt-16 backdrop-blur-sm transition-[margin] duration-300 ease-out-expo',
          sidebarCollapsed ? 'ml-16' : 'ml-64'
        )}
      >
        <div
          key={location.pathname}
          className="animate-page-in p-6 motion-reduce:animate-none"
        >
          {children}
        </div>
        <div className="border-t border-border/60 px-6 py-4 pb-8">
          <LegalFooter />
        </div>
      </main>
    </div>
  );
}
